/**
 * Catálogo de produtos (Task 11, brief 3.2) — lista das duas camadas.
 *
 * Cada linha mostra, lado a lado, o que é do operacional (SKU, categoria,
 * fornecedor, custo, preço-base: referência do Bling) e o que é da Plataforma
 * (nome comercial, publicação, tabelas B2B). Disponibilidade é SEMPRE a
 * consolidada (estoque vendável − reservas ativas, D-11.5), nunca estoque
 * bruto. A coluna de divergências leva à tela item a item do produto.
 *
 * Redesign Entrega 3 (apresentação apenas): tabela densa premium com
 * cabeçalho de seção, BUSCA, FILTROS (categoria/fornecedor/publicação/
 * disponibilidade) e PAGINAÇÃO — tudo client-side sobre o MESMO
 * `dataset.products` e com o estado na URL (`useSearchParams`), sem nova regra
 * nem chamada a `/api/*`. Disponibilidade e divergências entram como SELO
 * (ícone+texto), nunca só cor. Estados empty/awaiting_source/error desenhados.
 * Todos os hooks dos testes preservados (`data-page`, `data-record-count`,
 * `data-bling-source`, `data-availability`, `data-pending-divergences`, links).
 *
 * Migração para `.tsx` (Cluster B — Catálogo & Estoque, redesign por perfil):
 * mesmo markup/contrato de antes, só a sintaxe `createElement` → JSX. Import
 * de `React` explícito — ver nota equivalente em `layout/AppShell.tsx`.
 */
import React, { type ReactElement } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { RecordTable } from "../components/RecordTable.js";
import { CatalogBoundary } from "../components/CatalogBoundary.js";
import { StatusPanel } from "../components/StatusPanel.js";
import { TablePagination, TableToolbar } from "../components/TableToolbar.js";
import { AvailabilityBadge, DivergenceBadge, PublishedBadge } from "../components/Badges.js";
import { actionRequiredCountByProduct } from "../../../../packages/catalog/src/index.js";
import { availabilityOfProduct, currentPriceCentsOf, lowestB2bPriceCentsOf } from "../catalog/availability.js";
import { blingSourceLabel, catalogDivergencesOf, type CatalogDataset } from "../catalog/dataset.js";
import {
  availabilityBucketOf,
  categoryOptions,
  EMPTY_FILTERS,
  filtersFromParams,
  hasActiveFilters,
  paginateCatalog,
  supplierOptions,
  type CatalogListFilters
} from "../catalog/list-view.js";
import { formatBrl, formatCount } from "../format.js";
import type { CatalogViewProps } from "../catalog/view-props.js";
import type { PlatformCatalogProduct } from "../api/types.js";

/** Monta a query string da listagem a partir de um conjunto de filtros. */
function filtersToQuery(filters: CatalogListFilters): string {
  const params = new URLSearchParams();
  if (filters.search.trim()) params.set("busca", filters.search.trim());
  if (filters.category) params.set("categoria", filters.category);
  if (filters.supplier) params.set("fornecedor", filters.supplier);
  if (filters.published !== null) params.set("publicado", filters.published ? "sim" : "nao");
  if (filters.availability) params.set("disponibilidade", filters.availability);
  if (filters.page > 1) params.set("pagina", String(filters.page));
  const query = params.toString();
  return query ? `?${query}` : "";
}

export function CatalogoProdutosView({ dataset }: CatalogViewProps): ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = filtersFromParams(searchParams);

  if (dataset.sources.platformCatalog === "awaiting_contract") {
    return (
      <div className="page page--catalogo flex flex-col gap-6" data-page="catalogo-produtos">
        <header className="flex flex-col gap-1">
          <h2 className="text-2xl font-black text-ink sm:text-3xl">Catálogo e estoque</h2>
        </header>
        <StatusPanel
          kind="awaiting_source"
          title="Catálogo em duas camadas não publicado neste ambiente"
          detail="A rota /api/catalog/platform/products responde em desenvolvimento/E2E; no caminho de produção ela entra junto da paridade Worker → Edge Functions (handoff H-10.A). Sem a fonte não listamos produto — lista vazia aqui diria, falsamente, que não há catálogo."
        />
      </div>
    );
  }

  const divergences = catalogDivergencesOf(dataset);
  const pendingByProduct = divergences ? actionRequiredCountByProduct(divergences) : null;
  const products = dataset.products;
  const paged = paginateCatalog(products, filters);
  const active = hasActiveFilters(filters);

  /** Aplica um patch de filtros à URL. Mudar o RECORTE sempre volta à página 1. */
  function applyFilters(patch: Partial<CatalogListFilters>): void {
    const next: CatalogListFilters = { ...filters, ...patch };
    // Só a navegação de página mantém a página; qualquer outro filtro reseta.
    if (!("page" in patch)) next.page = 1;
    const query = filtersToQuery(next);
    setSearchParams(new URLSearchParams(query.startsWith("?") ? query.slice(1) : query));
  }

  return (
    <div className="page page--catalogo flex flex-col gap-6" data-page="catalogo-produtos">
      <header className="flex flex-col gap-1">
        <h2 className="text-2xl font-black text-ink sm:text-3xl">Catálogo e estoque</h2>
        <p className="drilldown-header__origin text-sm text-body" data-record-count={dataset.products.length}>
          {`${dataset.products.length} produto(s) no catálogo em duas camadas (operacional do Bling + camada comercial da Plataforma).`}
        </p>
        <p className="page__note text-sm text-muted" data-bling-source={dataset.snapshot?.source ?? "absent"}>
          {blingSourceLabel(dataset.snapshot)}
        </p>
      </header>
      <TableToolbar
        filters={filters}
        categories={categoryOptions(products)}
        suppliers={supplierOptions(products)}
        onChange={applyFilters}
        onReset={() => setSearchParams(new URLSearchParams())}
        active={active}
      />
      <RecordTable<PlatformCatalogProduct>
        caption="Produtos, disponibilidade consolidada e divergências por item"
        rows={paged.rows}
        idOf={(product) => product.productId}
        // Fase 8 (Plano de Ataque R8): rollout mecânico do primitivo da Fase 6.
        animateRows
        emptyMessage={
          active
            ? "Nenhum produto corresponde à busca e aos filtros aplicados. Ajuste ou limpe os filtros."
            : "Nenhum produto no catálogo."
        }
        columns={[
          {
            key: "sku",
            label: "SKU (Bling)",
            render: (product) => (
              <Link to={`/catalogo/${product.productId}`} className="font-semibold text-brand-text hover:underline">
                {product.operational.sku}
              </Link>
            )
          },
          {
            key: "commercialName",
            label: "Nome comercial (Plataforma)",
            render: (product) => product.commercial.commercialName ?? product.name
          },
          { key: "category", label: "Categoria", render: (product) => product.operational.category ?? "—" },
          { key: "supplier", label: "Fornecedor", render: (product) => product.operational.supplier ?? "—" },
          {
            key: "cost",
            label: "Custo",
            align: "right",
            render: (product) =>
              product.operational.costCents === null ? "custo não sincronizado" : formatBrl(product.operational.costCents)
          },
          {
            key: "basePrice",
            label: "Preço-base",
            align: "right",
            render: (product) =>
              product.operational.basePriceCents === null || product.operational.basePriceCents === 0
                ? "sem preço-base"
                : formatBrl(product.operational.basePriceCents)
          },
          {
            key: "price",
            label: "Preço vigente",
            align: "right",
            render: (product) => {
              const price = currentPriceCentsOf(product);
              return price === null ? "sem oferta vigente" : formatBrl(price);
            }
          },
          {
            key: "b2b",
            label: "Menor preço B2B",
            align: "right",
            render: (product) => {
              const b2b = lowestB2bPriceCentsOf(product);
              return b2b === null ? "sem tabela B2B" : formatBrl(b2b);
            }
          },
          {
            key: "availability",
            label: "Disponibilidade consolidada",
            render: (product) => {
              const availability = availabilityOfProduct(product);
              if (availability.sellableStock === null) {
                return <AvailabilityBadge bucket="indisponivel" consolidated={null} critical={false} detail="sem oferta vendável (0)" />;
              }
              return (
                <AvailabilityBadge
                  bucket={availabilityBucketOf(product)}
                  consolidated={availability.consolidated}
                  critical={availability.critical}
                  detail={`${formatCount(availability.consolidated)} (${formatCount(availability.sellableStock)} − ${formatCount(availability.activeReservations)} reservado)`}
                />
              );
            }
          },
          {
            key: "published",
            label: "Publicado na loja",
            render: (product) => <PublishedBadge published={product.commercial.published} />
          },
          {
            key: "divergences",
            label: "Divergências a tratar",
            render: (product) => {
              if (!pendingByProduct) return "sem comparação (operacional ausente)";
              const pending = pendingByProduct.get(product.productId) ?? 0;
              // O selo fica dentro de um Link para o produto: as props de `Link`
              // não aceitam o `data-*` arbitrário no tipo do react-router, então o
              // hook `data-pending-divergences` mora no próprio selo (Badges.tsx).
              return (
                <Link to={`/catalogo/${product.productId}`} className="inline-flex rounded-pill focus-visible:outline-none">
                  <DivergenceBadge pending={pending} />
                </Link>
              );
            }
          }
        ]}
      />
      <TablePagination page={paged} hrefForPage={(pageNumber) => `/catalogo${filtersToQuery({ ...filters, page: pageNumber })}`} />
    </div>
  );
}

export function CatalogoProdutos(): ReactElement {
  return <CatalogBoundary>{(dataset: CatalogDataset) => <CatalogoProdutosView dataset={dataset} />}</CatalogBoundary>;
}

// Reexporta os filtros vazios para testes/consumidores que montam a view com
// um estado conhecido sem depender da ordem de query params.
export { EMPTY_FILTERS };
